import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useSpin } from "@/lib/spin";
import SpinStage from "@/components/SpinStage";
import { levelLabel } from "@/lib/levels";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shuffle, Maximize2, X, Radio, MonitorPlay } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

function CategoryControls({ categories, categoryId, setCategoryId, sceneBuilder, setSceneBuilder, extra, toggleExtra }) {
  return (
    <>
      <div>
        <Label htmlFor="cat">Category</Label>
        <select id="cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="generator-category"
          className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf] bg-white">
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name} ({levelLabel(c.level)}) · {c.prompt_count} prompts</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={sceneBuilder} onCheckedChange={setSceneBuilder} data-testid="generator-scene-toggle" id="scene" />
        <Label htmlFor="scene" className="cursor-pointer">Scene Builder — combine multiple categories (opt-in)</Label>
      </div>
      {sceneBuilder && (
        <div className="bg-[#f9f6f6] rounded-lg p-4 border border-[#dfd8da]">
          <p className="text-sm font-semibold text-[#545454] mb-2">Add categories to combine:</p>
          <div className="flex flex-wrap gap-2">
            {categories.filter((c) => c.id !== categoryId).map((c) => (
              <button key={c.id} onClick={() => toggleExtra(c.id)} data-testid={`generator-extra-${c.id}`}
                className={`text-sm px-3 py-1.5 rounded-full font-semibold ${extra.includes(c.id) ? "bg-[#920e12] text-white" : "bg-white border border-[#d6cecf] text-[#545454]"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function ResultCard({ result, spinning, countdown, wheelItems, reducedMotion }) {
  if (!result) return null;
  return (
    <div className={`mt-6 bg-[#221517] text-white rounded-2xl p-8 text-center transition-transform duration-200 ${spinning ? "scale-[1.02]" : ""}`} data-testid="generator-result">
      <SpinStage result={result} spinning={spinning} countdown={countdown} wheelItems={wheelItems} reducedMotion={reducedMotion} />
    </div>
  );
}

export default function PromptGenerator() {
  const { sensoryFriendly } = useSettings();
  const { display, spinning, countdown, run } = useSpin(sensoryFriendly);
  const [mode, setMode] = useState("solo"); // solo | room
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [sceneBuilder, setSceneBuilder] = useState(false);
  const [extra, setExtra] = useState([]);
  const [projector, setProjector] = useState(false);
  const [room, setRoom] = useState(null); // {code}
  const sessionId = useRef(`sess_${Date.now()}`);

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCategories(data);
      if (data[0]) setCategoryId(data[0].id);
    }).catch(() => {});
  }, []);

  const wheelItems = categories.map((c) => c.name);
  const toggleExtra = (id) => setExtra((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  const prevSpinning = useRef(false);
  useEffect(() => {
    if (projector && prevSpinning.current && !spinning && display && !sensoryFriendly) {
      const colors = ["#920e12", "#ffdedf", "#bf6163", "#550001", "#ffffff"];
      confetti({ particleCount: 150, spread: 95, startVelocity: 45, origin: { y: 0.6 }, colors });
      setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0 }, colors }), 160);
      setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 70, origin: { x: 1 }, colors }), 160);
    }
    prevSpinning.current = spinning;
  }, [spinning, projector, display, sensoryFriendly]);

  const draw = async () => {
    if (!categoryId) return;
    try {
      const { data } = await api.post("/generator/draw", {
        category_id: categoryId, session_id: sessionId.current, exclude: [],
        scene_builder: sceneBuilder, extra_category_ids: extra,
      });
      run(data, data.spin_pool);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const startRoom = async () => {
    if (!categoryId) return;
    try {
      const { data } = await api.post("/generator/room", {
        category_id: categoryId, session_id: sessionId.current, exclude: [],
        scene_builder: sceneBuilder, extra_category_ids: extra,
      });
      setRoom(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const roomDraw = async () => {
    if (!room) return;
    try {
      const { data } = await api.post(`/generator/room/${room.code}/draw`);
      run(data, data.spin_pool);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const endRoom = async () => {
    if (!room) return;
    try { await api.post(`/generator/room/${room.code}/close`); } catch {}
    setRoom(null);
  };

  const playUrl = room ? `${window.location.origin}/play/${room.code}` : "";

  // Solo projector overlay
  if (projector) {
    return (
      <div className="fixed inset-0 z-50 stage-scrim text-white flex flex-col items-center justify-center p-8" data-testid="generator-projector">
        <button onClick={() => setProjector(false)} className="absolute top-6 right-6 text-[#dfd8da] hover:text-white" data-testid="generator-projector-exit"><X size={32} /></button>
        {display ? (
          <div className="text-center"><SpinStage result={display} spinning={spinning} countdown={countdown} projector wheelItems={wheelItems} reducedMotion={sensoryFriendly} /></div>
        ) : (
          <p className="font-runner text-4xl text-[#dfd8da]">Press Spin to begin</p>
        )}
        <Button onClick={draw} disabled={spinning} className="mt-16 h-16 px-10 text-2xl bg-[#920e12] hover:bg-[#bf6163] text-white font-black" data-testid="generator-projector-draw">
          <Shuffle className="mr-2" /> {spinning ? "Spinning…" : "Spin"}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="prompt-generator">
      <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]" data-testid="promptinator-tag">The Promptinator</span>
      <h1 className="mt-2 font-display text-4xl text-[#550001]">Spin For A Prompt</h1>

      {/* Mode switch */}
      <div className="mt-6 inline-flex rounded-xl border border-[#dfd8da] overflow-hidden bg-white" role="tablist">
        <button onClick={() => setMode("solo")} data-testid="generator-mode-solo"
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold ${mode === "solo" ? "bg-[#920e12] text-white" : "text-[#545454]"}`}>
          <MonitorPlay size={16} /> Director / Projector
        </button>
        <button onClick={() => setMode("room")} data-testid="generator-mode-room"
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold ${mode === "room" ? "bg-[#920e12] text-white" : "text-[#545454]"}`}>
          <Radio size={16} /> Draw Together on Phones
        </button>
      </div>

      {mode === "solo" ? (
        <>
          <p className="mt-4 text-[#545454]">Click Spin for a prompt. Hit Projector Mode for the full-screen version. The Sensory-Friendly mode in the top-right corner removes the spinning wheel and ticking sound.</p>
          <div className="mt-6 bg-white rounded-2xl border border-[#dfd8da] p-6 space-y-5">
            <CategoryControls {...{ categories, categoryId, setCategoryId, sceneBuilder, setSceneBuilder, extra, toggleExtra }} />
            <div className="flex gap-3">
              <Button onClick={draw} disabled={spinning} className="flex-1 h-12 bg-[#920e12] hover:bg-[#550001] text-white font-bold text-base" data-testid="generator-draw">
                <Shuffle className={`mr-2 ${spinning ? "animate-spin" : ""}`} size={18} /> {spinning ? "Spinning…" : "Spin For A Prompt"}
              </Button>
              <Button onClick={() => setProjector(true)} variant="outline" className="h-12 border-[#920e12] text-[#920e12]" data-testid="generator-projector-btn">
                <Maximize2 className="mr-2" size={18} /> Projector
              </Button>
            </div>
          </div>
          <ResultCard result={display} spinning={spinning} countdown={countdown} wheelItems={wheelItems} reducedMotion={sensoryFriendly} />
        </>
      ) : (
        <>
          <p className="mt-4 text-[#545454]">Start a room and show the code or QR on screen. Participants scan it on their phones and everyone sees the same prompt at the same time — great for online classes.</p>
          {!room ? (
            <div className="mt-6 bg-white rounded-2xl border border-[#dfd8da] p-6 space-y-5">
              <CategoryControls {...{ categories, categoryId, setCategoryId, sceneBuilder, setSceneBuilder, extra, toggleExtra }} />
              <Button onClick={startRoom} className="w-full h-12 bg-[#920e12] hover:bg-[#550001] text-white font-bold text-base" data-testid="generator-start-room">
                <Radio className="mr-2" size={18} /> Start live room
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              <div className="bg-[#550001] text-white rounded-2xl p-8" data-testid="room-code-panel">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#bf6163]">Room code</div>
                    <div className="mt-2 font-timer text-7xl font-black tracking-[0.2em]" data-testid="room-code">{room.code}</div>
                    <p className="mt-4 text-[#ffdedf]">Open <strong>{window.location.host}/play</strong> and enter this code.</p>
                    <div className="mt-1 text-sm text-[#dfd8da] break-all">{playUrl}</div>
                  </div>
                  <div className="text-center">
                    <div className="bg-white p-3 rounded-xl inline-block" data-testid="room-qr">
                      <QRCodeSVG value={playUrl} size={148} bgColor="#ffffff" fgColor="#550001" level="M" />
                    </div>
                    <div className="mt-2 text-sm font-bold text-[#ffdedf]">Scan to join</div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={roomDraw} disabled={spinning} className="flex-1 h-14 bg-[#920e12] hover:bg-[#bf6163] text-white font-black text-lg" data-testid="room-draw">
                  <Shuffle className={`mr-2 ${spinning ? "animate-spin" : ""}`} size={20} /> {spinning ? "Spinning…" : "Spin for the room"}
                </Button>
                <Button onClick={endRoom} variant="outline" className="h-14 border-[#991b1b] text-[#991b1b]" data-testid="room-end">End</Button>
              </div>
              <ResultCard result={display} spinning={spinning} countdown={countdown} wheelItems={wheelItems} reducedMotion={sensoryFriendly} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
